import { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { createChart, CandlestickSeries, AreaSeries, LineSeries, HistogramSeries, ColorType } from "lightweight-charts";

function aggregateToOHLCV(historyData, interval) {
  // For intraday intervals, use UTC timestamps directly
  const isIntraday = ["5m", "10m", "30m", "1h", "4h", "12h"].includes(interval);

  if (isIntraday) {
    // Data already comes bucketed from backend - just format for lightweight-charts
    const result = [];
    const seen = new Set();
    for (const h of historyData) {
      const ts = Math.floor((h.timestamp || new Date(h.date).getTime()) / 1000);
      if (seen.has(ts)) continue;
      seen.add(ts);
      result.push({
        time: ts,
        open: h.open || h.close,
        high: h.high || h.close,
        low: h.low || h.close,
        close: h.close,
        volume: h.volume || 0,
      });
    }
    return result.sort((a, b) => a.time - b.time);
  }

  // Daily+ aggregation (original logic)
  const dayMap = new Map();
  for (const h of historyData) {
    const dateStr = h.date
      ? h.date.split(" ")[0]
      : new Date(h.timestamp).toISOString().split("T")[0];
    if (!dayMap.has(dateStr)) {
      dayMap.set(dateStr, { open: h.open || h.close, high: h.high || h.close, low: h.low || h.close, close: h.close, volume: h.volume || 0 });
    } else {
      const e = dayMap.get(dateStr);
      e.high = Math.max(e.high, h.high || h.close);
      e.low = Math.min(e.low, h.low || h.close);
      e.close = h.close;
      e.volume = (e.volume || 0) + (h.volume || 0);
    }
  }
  return Array.from(dayMap.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([date, d]) => ({ time: date, ...d }));
}

function calcSMA(data, period) {
  const r = [];
  for (let i = period - 1; i < data.length; i++) {
    r.push({ time: data[i].time, value: data.slice(i - period + 1, i + 1).reduce((s, d) => s + d.close, 0) / period });
  }
  return r;
}

function calcEMA(data, period) {
  if (data.length === 0) return [];
  const mult = 2 / (period + 1);
  const r = [{ time: data[0].time, value: data[0].close }];
  for (let i = 1; i < data.length; i++) {
    r.push({ time: data[i].time, value: data[i].close * mult + r[i - 1].value * (1 - mult) });
  }
  return r.slice(Math.min(period - 1, r.length - 1));
}

function calcBollinger(data, period = 20, stdDev = 2) {
  const upper = [], lower = [], mid = [];
  for (let i = period - 1; i < data.length; i++) {
    const slice = data.slice(i - period + 1, i + 1);
    const avg = slice.reduce((s, d) => s + d.close, 0) / period;
    const variance = slice.reduce((s, d) => s + Math.pow(d.close - avg, 2), 0) / period;
    const sd = Math.sqrt(variance);
    mid.push({ time: data[i].time, value: avg });
    upper.push({ time: data[i].time, value: avg + stdDev * sd });
    lower.push({ time: data[i].time, value: avg - stdDev * sd });
  }
  return { upper, lower, mid };
}

function calcRSI(data, period = 14) {
  if (data.length < period + 1) return [];
  const changes = [];
  for (let i = 1; i < data.length; i++) changes.push(data[i].close - data[i - 1].close);
  let avgGain = 0, avgLoss = 0;
  for (let i = 0; i < period; i++) {
    if (changes[i] > 0) avgGain += changes[i];
    else avgLoss += Math.abs(changes[i]);
  }
  avgGain /= period;
  avgLoss /= period;
  const r = [{ time: data[period].time, value: avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss) }];
  for (let i = period; i < changes.length; i++) {
    const gain = changes[i] > 0 ? changes[i] : 0;
    const loss = changes[i] < 0 ? Math.abs(changes[i]) : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    r.push({ time: data[i + 1].time, value: avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss) });
  }
  return r;
}

function calcMACD(data, fast = 12, slow = 26, signal = 9) {
  if (data.length < slow) return { macdLine: [], signalLine: [], histogram: [] };
  const emaF = calcEMA(data, fast), emaS = calcEMA(data, slow);
  const macdLine = [];
  const sMap = new Map(emaS.map(d => [d.time, d.value]));
  for (const f of emaF) {
    const sv = sMap.get(f.time);
    if (sv !== undefined) macdLine.push({ time: f.time, value: f.value - sv });
  }
  const signalLine = [];
  if (macdLine.length >= signal) {
    const mult = 2 / (signal + 1);
    signalLine.push({ time: macdLine[0].time, value: macdLine[0].value });
    for (let i = 1; i < macdLine.length; i++) {
      signalLine.push({ time: macdLine[i].time, value: macdLine[i].value * mult + signalLine[i - 1].value * (1 - mult) });
    }
  }
  const histogram = [];
  const sigMap = new Map(signalLine.map(d => [d.time, d.value]));
  for (const m of macdLine) {
    const sv = sigMap.get(m.time);
    if (sv !== undefined) {
      const val = m.value - sv;
      histogram.push({ time: m.time, value: val, color: val >= 0 ? "rgba(16,185,129,0.5)" : "rgba(239,68,68,0.5)" });
    }
  }
  return { macdLine, signalLine, histogram };
}

function calcVWAP(data) {
  let cumVol = 0, cumPV = 0;
  return data.filter(d => d.volume > 0).map(d => {
    const tp = (d.high + d.low + d.close) / 3;
    cumPV += tp * d.volume;
    cumVol += d.volume;
    return { time: d.time, value: cumVol > 0 ? cumPV / cumVol : tp };
  });
}

const INDICATORS = [
  { id: "sma", label: "SMA", group: "overlay" },
  { id: "ema", label: "EMA", group: "overlay" },
  { id: "bb", label: "Bollinger", group: "overlay" },
  { id: "vwap", label: "VWAP", group: "overlay" },
  { id: "rsi", label: "RSI", group: "sub" },
  { id: "macd", label: "MACD", group: "sub" },
];

export default function TradingViewChart({ history, isPositive, interval = "auto" }) {
  const chartContainerRef = useRef(null);
  const rsiContainerRef = useRef(null);
  const macdContainerRef = useRef(null);
  const chartRef = useRef(null);
  const rsiChartRef = useRef(null);
  const macdChartRef = useRef(null);
  const [chartType, setChartType] = useState("candles");
  const [activeIndicators, setActiveIndicators] = useState(["sma"]);
  const [measureMode, setMeasureMode] = useState(false);
  const [measurePoints, setMeasurePoints] = useState([]);
  const [measureResult, setMeasureResult] = useState(null);

  const toggleIndicator = (id) => {
    setActiveIndicators(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const { daily, sma20, sma50, ema12, ema26, bollinger, rsi, macd, vwap } = useMemo(() => {
    if (!history || history.length === 0) return { daily: [], sma20: [], sma50: [], ema12: [], ema26: [], bollinger: { upper: [], lower: [], mid: [] }, rsi: [], macd: { macdLine: [], signalLine: [], histogram: [] }, vwap: [] };
    const d = aggregateToOHLCV(history, interval);
    return {
      daily: d, sma20: calcSMA(d, 20), sma50: calcSMA(d, 50),
      ema12: calcEMA(d, 12), ema26: calcEMA(d, 26),
      bollinger: calcBollinger(d), rsi: calcRSI(d), macd: calcMACD(d), vwap: calcVWAP(d),
    };
  }, [history, interval]);

  const buildChart = useCallback(() => {
    if (!chartContainerRef.current || daily.length === 0) return;

    // Cleanup
    [chartRef, rsiChartRef, macdChartRef].forEach(ref => { if (ref.current) { ref.current.remove(); ref.current = null; } });

    const sharedOpts = {
      autoSize: true,
      layout: { background: { type: ColorType.Solid, color: "transparent" }, textColor: "#71717a", fontFamily: "'JetBrains Mono', monospace", fontSize: 10 },
      grid: { vertLines: { color: "rgba(63,63,70,0.15)" }, horzLines: { color: "rgba(63,63,70,0.15)" } },
      crosshair: { mode: 0, vertLine: { color: "rgba(59,130,246,0.4)", width: 1, style: 2, labelBackgroundColor: "#1e40af" }, horzLine: { color: "rgba(59,130,246,0.4)", width: 1, style: 2, labelBackgroundColor: "#1e40af" } },
      timeScale: { borderColor: "#27272a", timeVisible: ["5m","10m","30m","1h","4h","12h"].includes(interval), secondsVisible: false },
    };

    // Main chart
    const chart = createChart(chartContainerRef.current, {
      ...sharedOpts,
      rightPriceScale: { borderColor: "#27272a", scaleMargins: { top: 0.05, bottom: 0.25 } },
    });
    chartRef.current = chart;

    if (chartType === "candles") {
      const cs = chart.addSeries(CandlestickSeries, { upColor: "#10b981", downColor: "#ef4444", borderUpColor: "#10b981", borderDownColor: "#ef4444", wickUpColor: "#10b981", wickDownColor: "#ef4444" });
      cs.setData(daily.map(d => ({ time: d.time, open: d.open, high: d.high, low: d.low, close: d.close })));
    } else {
      const as = chart.addSeries(AreaSeries, { lineColor: isPositive ? "#10b981" : "#ef4444", topColor: isPositive ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.15)", bottomColor: "transparent", lineWidth: 2 });
      as.setData(daily.map(d => ({ time: d.time, value: d.close })));
    }

    // SMA
    if (activeIndicators.includes("sma")) {
      if (sma20.length > 0) { const s = chart.addSeries(LineSeries, { color: "rgba(59,130,246,0.7)", lineWidth: 1, priceLineVisible: false, lastValueVisible: false }); s.setData(sma20); }
      if (sma50.length > 0) { const s = chart.addSeries(LineSeries, { color: "rgba(250,204,21,0.7)", lineWidth: 1, priceLineVisible: false, lastValueVisible: false }); s.setData(sma50); }
    }

    // EMA
    if (activeIndicators.includes("ema")) {
      if (ema12.length > 0) { const s = chart.addSeries(LineSeries, { color: "rgba(168,85,247,0.7)", lineWidth: 1, priceLineVisible: false, lastValueVisible: false }); s.setData(ema12); }
      if (ema26.length > 0) { const s = chart.addSeries(LineSeries, { color: "rgba(236,72,153,0.7)", lineWidth: 1, priceLineVisible: false, lastValueVisible: false }); s.setData(ema26); }
    }

    // Bollinger
    if (activeIndicators.includes("bb") && bollinger.upper.length > 0) {
      chart.addSeries(LineSeries, { color: "rgba(34,211,238,0.4)", lineWidth: 1, lineStyle: 2, priceLineVisible: false, lastValueVisible: false }).setData(bollinger.upper);
      chart.addSeries(LineSeries, { color: "rgba(34,211,238,0.4)", lineWidth: 1, lineStyle: 2, priceLineVisible: false, lastValueVisible: false }).setData(bollinger.lower);
      chart.addSeries(LineSeries, { color: "rgba(34,211,238,0.25)", lineWidth: 1, priceLineVisible: false, lastValueVisible: false }).setData(bollinger.mid);
    }

    // VWAP
    if (activeIndicators.includes("vwap") && vwap.length > 0) {
      chart.addSeries(LineSeries, { color: "rgba(251,146,60,0.7)", lineWidth: 1.5, lineStyle: 0, priceLineVisible: false, lastValueVisible: false }).setData(vwap);
    }

    // Volume
    const vs = chart.addSeries(HistogramSeries, { priceFormat: { type: "volume" }, priceScaleId: "volume" });
    vs.setData(daily.map(d => ({ time: d.time, value: d.volume, color: d.close >= d.open ? "rgba(16,185,129,0.25)" : "rgba(239,68,68,0.25)" })));
    chart.priceScale("volume").applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });

    chart.timeScale().fitContent();

    // RSI sub-chart
    if (activeIndicators.includes("rsi") && rsiContainerRef.current && rsi.length > 0) {
      const rc = createChart(rsiContainerRef.current, {
        ...sharedOpts, rightPriceScale: { borderColor: "#27272a", scaleMargins: { top: 0.1, bottom: 0.1 } },
      });
      rsiChartRef.current = rc;
      const rs = rc.addSeries(LineSeries, { color: "#a78bfa", lineWidth: 1.5, priceLineVisible: false, lastValueVisible: true });
      rs.setData(rsi);
      // Overbought/Oversold lines
      const ob = rsi.map(d => ({ time: d.time, value: 70 }));
      const os = rsi.map(d => ({ time: d.time, value: 30 }));
      rc.addSeries(LineSeries, { color: "rgba(239,68,68,0.3)", lineWidth: 1, lineStyle: 2, priceLineVisible: false, lastValueVisible: false }).setData(ob);
      rc.addSeries(LineSeries, { color: "rgba(16,185,129,0.3)", lineWidth: 1, lineStyle: 2, priceLineVisible: false, lastValueVisible: false }).setData(os);
      rc.timeScale().fitContent();
      // Sync time scales
      chart.timeScale().subscribeVisibleLogicalRangeChange(range => { if (range) rc.timeScale().setVisibleLogicalRange(range); });
      rc.timeScale().subscribeVisibleLogicalRangeChange(range => { if (range) chart.timeScale().setVisibleLogicalRange(range); });
    }

    // MACD sub-chart
    if (activeIndicators.includes("macd") && macdContainerRef.current && macd.macdLine.length > 0) {
      const mc = createChart(macdContainerRef.current, {
        ...sharedOpts, rightPriceScale: { borderColor: "#27272a", scaleMargins: { top: 0.1, bottom: 0.1 } },
      });
      macdChartRef.current = mc;
      mc.addSeries(LineSeries, { color: "#3b82f6", lineWidth: 1.5, priceLineVisible: false, lastValueVisible: false }).setData(macd.macdLine);
      mc.addSeries(LineSeries, { color: "#f97316", lineWidth: 1, priceLineVisible: false, lastValueVisible: false }).setData(macd.signalLine);
      mc.addSeries(HistogramSeries, { priceFormat: { type: "volume" } }).setData(macd.histogram);
      mc.timeScale().fitContent();
      chart.timeScale().subscribeVisibleLogicalRangeChange(range => { if (range) mc.timeScale().setVisibleLogicalRange(range); });
      mc.timeScale().subscribeVisibleLogicalRangeChange(range => { if (range) chart.timeScale().setVisibleLogicalRange(range); });
    }

    // Measure mode: click to set points
    if (measureMode) {
      chart.subscribeCrosshairMove(param => {
        if (!param.time || !param.point) return;
        // Store crosshair position for measure
        chartContainerRef.current._lastCrosshair = { time: param.time, price: param.seriesData?.values()?.next()?.value?.close || param.seriesData?.values()?.next()?.value?.value || 0 };
      });
    }
  }, [daily, sma20, sma50, ema12, ema26, bollinger, rsi, macd, vwap, chartType, activeIndicators, isPositive, measureMode]);

  useEffect(() => {
    buildChart();
    return () => {
      [chartRef, rsiChartRef, macdChartRef].forEach(ref => { if (ref.current) { ref.current.remove(); ref.current = null; } });
    };
  }, [buildChart]);

  // Measure tool click handler
  const handleChartClick = useCallback((e) => {
    if (!measureMode || !chartRef.current) return;
    const rect = chartContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const ts = chartRef.current.timeScale();
    const ps = chartRef.current.priceScale("right");
    // Get the coordinate
    const time = ts.coordinateToTime(x);
    // Approximate price from y
    const topPrice = ps ? 0 : 0;
    // Use crosshair data instead
    const crosshair = chartContainerRef.current._lastCrosshair;
    if (!crosshair) return;

    setMeasurePoints(prev => {
      if (prev.length === 0) {
        return [crosshair];
      } else {
        const p1 = prev[0];
        const p2 = crosshair;
        const priceDiff = p2.price - p1.price;
        const pctChange = p1.price !== 0 ? ((priceDiff / p1.price) * 100).toFixed(2) : 0;
        setMeasureResult({ from: p1, to: p2, diff: priceDiff, pct: pctChange });
        return [];
      }
    });
  }, [measureMode]);

  if (!history || history.length === 0) {
    return <div className="h-[400px] flex items-center justify-center text-zinc-600 text-xs">No chart data available</div>;
  }

  const hasSubChart = activeIndicators.includes("rsi") || activeIndicators.includes("macd");

  return (
    <div>
      {/* Controls */}
      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        {/* Chart type */}
        <div className="flex bg-zinc-800/50 rounded-sm p-0.5">
          {["candles", "area"].map(t => (
            <button key={t} data-testid={`chart-type-${t}`} onClick={() => setChartType(t)}
              className={`text-[10px] px-2 py-1 rounded-sm transition-colors ${chartType === t ? "bg-zinc-700 text-zinc-200" : "text-zinc-500 hover:text-zinc-300"}`}>
              {t === "candles" ? "Candles" : "Area"}
            </button>
          ))}
        </div>

        <div className="w-px h-5 bg-zinc-800 mx-0.5" />

        {/* Indicators */}
        {INDICATORS.map(ind => (
          <button key={ind.id} data-testid={`toggle-${ind.id}`} onClick={() => toggleIndicator(ind.id)}
            className={`text-[10px] px-2 py-1 rounded-sm transition-colors border ${
              activeIndicators.includes(ind.id)
                ? ind.group === "overlay"
                  ? "border-blue-500/30 text-blue-400 bg-blue-500/8"
                  : "border-violet-500/30 text-violet-400 bg-violet-500/8"
                : "border-zinc-800 text-zinc-500 hover:text-zinc-400 bg-transparent"
            }`}>
            {ind.label}
          </button>
        ))}

        <div className="w-px h-5 bg-zinc-800 mx-0.5" />

        {/* Measure tool */}
        <button data-testid="measure-tool-btn" onClick={() => { setMeasureMode(!measureMode); setMeasurePoints([]); setMeasureResult(null); }}
          className={`text-[10px] px-2 py-1 rounded-sm transition-colors border flex items-center gap-1 ${
            measureMode ? "border-amber-500/30 text-amber-400 bg-amber-500/8" : "border-zinc-800 text-zinc-500 hover:text-zinc-400"
          }`}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><line x1="1" y1="9" x2="9" y2="1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="1" cy="9" r="1" fill="currentColor"/><circle cx="9" cy="1" r="1" fill="currentColor"/></svg>
          Measure
        </button>
      </div>

      {/* Indicator legends */}
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        {activeIndicators.includes("sma") && <>
          <span className="flex items-center gap-1 text-[9px] text-zinc-500"><span className="w-3 h-[2px] bg-blue-400/70 inline-block rounded" />SMA 20</span>
          <span className="flex items-center gap-1 text-[9px] text-zinc-500"><span className="w-3 h-[2px] bg-yellow-400/70 inline-block rounded" />SMA 50</span>
        </>}
        {activeIndicators.includes("ema") && <>
          <span className="flex items-center gap-1 text-[9px] text-zinc-500"><span className="w-3 h-[2px] bg-purple-400/70 inline-block rounded" />EMA 12</span>
          <span className="flex items-center gap-1 text-[9px] text-zinc-500"><span className="w-3 h-[2px] bg-pink-400/70 inline-block rounded" />EMA 26</span>
        </>}
        {activeIndicators.includes("bb") && <span className="flex items-center gap-1 text-[9px] text-zinc-500"><span className="w-3 h-[2px] bg-cyan-400/40 inline-block rounded" />Bollinger (20,2)</span>}
        {activeIndicators.includes("vwap") && <span className="flex items-center gap-1 text-[9px] text-zinc-500"><span className="w-3 h-[2px] bg-orange-400/70 inline-block rounded" />VWAP</span>}
      </div>

      {/* Measure result */}
      {measureMode && (
        <div className="mb-2 px-2.5 py-1.5 bg-amber-500/5 border border-amber-500/20 rounded-sm" data-testid="measure-info">
          {measureResult ? (
            <div className="flex items-center gap-3 text-[10px]">
              <span className="text-zinc-400">Measurement:</span>
              <span className={`font-data font-medium ${measureResult.diff >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {measureResult.diff >= 0 ? "+" : ""}{typeof measureResult.diff === 'number' ? measureResult.diff.toFixed(2) : measureResult.diff}
              </span>
              <span className={`font-data font-medium ${parseFloat(measureResult.pct) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                ({measureResult.pct > 0 ? "+" : ""}{measureResult.pct}%)
              </span>
              <button onClick={() => { setMeasureResult(null); setMeasurePoints([]); }} className="text-zinc-500 hover:text-zinc-300 ml-auto text-[9px]">Reset</button>
            </div>
          ) : (
            <p className="text-[10px] text-amber-400/70">
              {measurePoints.length === 0 ? "Click chart to set start point" : "Click chart to set end point"}
            </p>
          )}
        </div>
      )}

      {/* Main Chart */}
      <div ref={chartContainerRef} className="h-[400px]" data-testid="tv-chart" onClick={handleChartClick} style={{ cursor: measureMode ? "crosshair" : "default" }} />

      {/* RSI Sub-chart */}
      {activeIndicators.includes("rsi") && (
        <div className="mt-1">
          <p className="text-[9px] text-zinc-600 mb-0.5 flex items-center gap-1">
            <span className="w-2 h-[2px] bg-violet-400 inline-block rounded" />RSI (14)
            {rsi.length > 0 && <span className={`font-data ml-1 ${rsi[rsi.length-1].value > 70 ? "text-red-400" : rsi[rsi.length-1].value < 30 ? "text-emerald-400" : "text-zinc-400"}`}>{rsi[rsi.length-1].value.toFixed(1)}</span>}
          </p>
          <div ref={rsiContainerRef} className="h-[100px]" data-testid="rsi-chart" />
        </div>
      )}

      {/* MACD Sub-chart */}
      {activeIndicators.includes("macd") && (
        <div className="mt-1">
          <p className="text-[9px] text-zinc-600 mb-0.5 flex items-center gap-2">
            <span className="flex items-center gap-0.5"><span className="w-2 h-[2px] bg-blue-500 inline-block rounded" />MACD</span>
            <span className="flex items-center gap-0.5"><span className="w-2 h-[2px] bg-orange-500 inline-block rounded" />Signal</span>
          </p>
          <div ref={macdContainerRef} className="h-[100px]" data-testid="macd-chart" />
        </div>
      )}
    </div>
  );
}
